import React, { useState, useRef } from 'react'
import { useMutation, useQuery, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
// core components
import {
  createInfluencer,
  editInfluencer,
  getInfluencers,
  getCoupons
} from '../../apollo'
import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import {
  Box,
  Switch,
  Typography,
  Input,
  Alert,
  Select,
  MenuItem,
  Button,
  Grid,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton
} from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import RecordVoiceOverOutlined from '@mui/icons-material/RecordVoiceOverOutlined'
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded'
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined'
import FormCardHeader from '../FormCardHeader'
import { raisedButtonSx } from '../UI/Depth'

// Small icon + caption that opens each group of fields in the form.
const GroupHeading = ({ icon: Icon, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mt: 3,
      mb: 0.5,
      pb: 1,
      borderBottom: '1px dashed',
      borderColor: 'divider'
    }}>
    <Icon sx={{ fontSize: 18, color: 'primary.main' }} />
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
      {children}
    </Typography>
  </Box>
)

const CREATE_INFLUENCER = gql`
  ${createInfluencer}
`
const EDIT_INFLUENCER = gql`
  ${editInfluencer}
`
const GET_INFLUENCERS = gql`
  ${getInfluencers}
`
const GET_COUPONS = gql`
  ${getCoupons}
`

function Influencer(props) {
  const formRef = useRef()
  const mutation = props.influencer ? EDIT_INFLUENCER : CREATE_INFLUENCER

  const name = props.influencer ? props.influencer.name : ''
  const email = props.influencer ? props.influencer.email : ''
  const password = props.influencer ? props.influencer.password : ''
  const phone = props.influencer ? props.influencer.phone : ''

  const [mainError, mainErrorSetter] = useState('')
  const [success, successSetter] = useState('')
  const [nameError, nameErrorSetter] = useState(null)
  const [emailError, emailErrorSetter] = useState(null)
  const [passwordError, passwordErrorSetter] = useState(null)
  const [rateError, rateErrorSetter] = useState(null)
  const [couponError, couponErrorSetter] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [influencerEnabled, setInfluencerEnabled] = useState(
    props.influencer ? props.influencer.enabled : true
  )
  const bank = (props.influencer && props.influencer.bankDetails) || {}
  const [accountHolderName, setAccountHolderName] = useState(
    bank.accountHolderName || ''
  )
  const [bankName, setBankName] = useState(bank.bankName || '')
  const [accountNumber, setAccountNumber] = useState(bank.accountNumber || '')
  const [sortCode, setSortCode] = useState(bank.sortCode || '')
  const [influencerCoupons, setInfluencerCoupons] = useState(
    props.influencer && props.influencer.coupons
      ? props.influencer.coupons.map(coupon => coupon._id)
      : []
  )

  const onCompleted = data => {
    if (!props.influencer) clearFields()
    const message = props.influencer
      ? t('InfluencerUpdatedSuccessfully')
      : t('InfluencerAddedSuccessfully')
    mainErrorSetter('')
    successSetter(message)
    setTimeout(hideAlert, 3000)
    // Only a save that worked closes the dialog; errors stay on screen
    if (props.onClose) setTimeout(props.onClose, 1200)
  }
  const onError = ({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      mainErrorSetter(graphQLErrors[0].message)
    }
    if (networkError) {
      mainErrorSetter(networkError.result.errors[0].message)
    }
    successSetter('')
    setTimeout(hideAlert, 3000)
  }
  const [mutate, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_INFLUENCERS }],
    onError,
    onCompleted
  })
  const { data: couponData } = useQuery(GET_COUPONS)

  const validateName = value => !!value && value.trim().length > 0
  const validateEmail = value => /^\S+@\S+\.\S+$/.test((value || '').trim())
  const validatePassword = value => !!value && value.length >= 6
  const validateRate = value => value !== '' && Number(value) >= 0
  const validateCoupons = value => !!value && value.length > 0

  const onSubmitValidation = () => {
    const isNameValid = validateName(formRef.current['input-name'].value)
    const isEmailValid = validateEmail(formRef.current['input-email'].value)
    const isPasswordValid = validatePassword(
      formRef.current['input-password'].value
    )
    const isRateValid = validateRate(formRef.current['input-rate'].value)
    const areCouponsValid = validateCoupons(influencerCoupons)

    nameErrorSetter(isNameValid)
    emailErrorSetter(isEmailValid)
    passwordErrorSetter(isPasswordValid)
    rateErrorSetter(isRateValid)
    couponErrorSetter(areCouponsValid)

    return (
      isNameValid &&
      isEmailValid &&
      isPasswordValid &&
      isRateValid &&
      areCouponsValid
    )
  }

  const clearFields = () => {
    formRef.current.reset()
    setInfluencerCoupons([])
    setAccountHolderName('')
    setBankName('')
    setAccountNumber('')
    setSortCode('')
    nameErrorSetter(null)
    emailErrorSetter(null)
    passwordErrorSetter(null)
    rateErrorSetter(null)
    couponErrorSetter(null)
  }

  const hideAlert = () => {
    mainErrorSetter('')
    successSetter('')
  }
  const { t } = props

  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  const fieldClass = state => [
    globalClasses.input,
    state === false
      ? globalClasses.inputError
      : state === true
      ? globalClasses.inputSuccess
      : ''
  ]

  const couponList = couponData ? couponData.coupons : []
  const couponLabel = coupon =>
    `${coupon.title} (${
      coupon.discountType === 'PERCENTAGE'
        ? `${coupon.discount}%`
        : `£${coupon.discount}`
    })`

  const onSave = e => {
    e.preventDefault()
    if (!onSubmitValidation()) return
    mutate({
      variables: {
        influencerInput: {
          _id: props.influencer ? props.influencer._id : '',
          name: formRef.current['input-name'].value,
          email: formRef.current['input-email'].value,
          password: formRef.current['input-password'].value,
          phone: formRef.current['input-phone'].value,
          coupons: influencerCoupons,
          ratePerOrder: Number(formRef.current['input-rate'].value),
          enabled: influencerEnabled,
          bankDetails: {
            accountHolderName: accountHolderName.trim(),
            bankName: bankName.trim(),
            accountNumber: accountNumber.trim(),
            sortCode: sortCode.trim()
          }
        }
      }
    })
  }

  return (
    <Box className={classes.container}>
      <FormCardHeader
        className={classes.heading}
        icon={RecordVoiceOverOutlined}
        title={props.influencer ? t('EditInfluencer') : t('AddInfluencer')}
        subtitle={t('InfluencerFormSubtitle')}
        onClose={props.onClose}
        action={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              pl: 1.5,
              pr: 0.5,
              height: 36,
              borderRadius: 999,
              border: '1px solid',
              borderColor: influencerEnabled
                ? 'rgba(21,128,61,0.3)'
                : 'divider',
              bgcolor: influencerEnabled
                ? 'rgba(21,128,61,0.06)'
                : 'common.white'
            }}>
            <Typography
              component="label"
              htmlFor="input-enabled"
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: influencerEnabled ? '#15803D' : 'text.disabled'
              }}>
              {influencerEnabled ? t('Enabled') : t('Disabled')}
            </Typography>
            <Switch
              size="small"
              checked={influencerEnabled}
              onChange={() => setInfluencerEnabled(!influencerEnabled)}
              id="input-enabled"
              name="input-enabled"
            />
          </Box>
        }
      />

      <Box className={classes.form}>
        <form ref={formRef} onSubmit={onSave}>
          <GroupHeading icon={PersonOutlineRounded}>
            {t('AccountDetails')}
          </GroupHeading>
          <Grid container columnSpacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.labelText}>{t('Name')}</Typography>
              <Input
                id="input-name"
                name="input-name"
                placeholder={t('InfluencerName')}
                type="text"
                defaultValue={name}
                onBlur={event =>
                  nameErrorSetter(validateName(event.target.value))
                }
                disableUnderline
                className={fieldClass(nameError)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.labelText}>
                {t('EmailLogin')}
              </Typography>
              <Input
                id="input-email"
                name="input-email"
                placeholder={t('Email')}
                type="email"
                defaultValue={email}
                onBlur={event =>
                  emailErrorSetter(validateEmail(event.target.value))
                }
                disableUnderline
                className={fieldClass(emailError)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.labelText}>
                {t('Password')}
              </Typography>
              <Input
                id="input-password"
                name="input-password"
                placeholder={t('Password')}
                type={showPassword ? 'text' : 'password'}
                defaultValue={password}
                onBlur={event =>
                  passwordErrorSetter(validatePassword(event.target.value))
                }
                disableUnderline
                className={fieldClass(passwordError)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? (
                        <VisibilityIcon fontSize="small" />
                      ) : (
                        <VisibilityOffIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.labelText}>
                {t('Number')}
              </Typography>
              <Input
                id="input-phone"
                name="input-phone"
                placeholder={t('PhoneNumber')}
                type="tel"
                defaultValue={phone}
                disableUnderline
                className={globalClasses.input}
                pattern="[0-9]*"
              />
            </Grid>
          </Grid>

          <GroupHeading icon={LocalOfferOutlined}>
            {t('CommissionAndCoupons')}
          </GroupHeading>
          <Grid container columnSpacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography className={classes.labelText}>
                {t('RatePerOrder')}
              </Typography>
              <Input
                id="input-rate"
                name="input-rate"
                placeholder="0.00"
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                defaultValue={
                  props.influencer ? props.influencer.ratePerOrder : ''
                }
                onBlur={event =>
                  rateErrorSetter(validateRate(event.target.value))
                }
                startAdornment={
                  <InputAdornment position="start">£</InputAdornment>
                }
                disableUnderline
                className={fieldClass(rateError)}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography className={classes.labelText}>
                {t('SelectCoupons')}
              </Typography>
              <Select
                id="input-coupons"
                name="input-coupons"
                multiple
                displayEmpty
                disableUnderline
                inputProps={{ 'aria-label': t('SelectCoupons') }}
                value={influencerCoupons}
                onChange={e =>
                  setInfluencerCoupons(
                    typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : e.target.value
                  )
                }
                renderValue={selected => {
                  if (!selected.length) {
                    return (
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        {t('SelectCoupons')}
                      </Box>
                    )
                  }
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        flexWrap: 'nowrap',
                        overflow: 'hidden'
                      }}>
                      {couponList
                        .filter(coupon => selected.includes(coupon._id))
                        .map(coupon => (
                          <Chip
                            key={coupon._id}
                            size="small"
                            label={coupon.title}
                            sx={{
                              height: 24,
                              fontWeight: 700,
                              bgcolor: 'rgba(244,102,31,0.1)',
                              color: 'primary.main'
                            }}
                          />
                        ))}
                    </Box>
                  )
                }}
                MenuProps={{
                  PaperProps: { sx: { borderRadius: '12px', mt: 0.5 } }
                }}
                className={fieldClass(couponError)}
                sx={{
                  '& fieldset': { border: 'none' },
                  '& .MuiSelect-select': { py: 0, pl: 0 }
                }}>
                {couponList.map(coupon => (
                  <MenuItem
                    value={coupon._id}
                    key={coupon._id}
                    sx={{ fontSize: 14 }}>
                    <Checkbox
                      size="small"
                      checked={influencerCoupons.indexOf(coupon._id) > -1}
                    />
                    {couponLabel(coupon)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>

          {success || mainError ? (
            <Alert
              severity={mainError ? 'error' : 'success'}
              sx={{ mt: 3, borderRadius: '12px' }}>
              {mainError || success}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              mt: 3,
              pt: 2.5,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
            {props.onClose ? (
              <Button
                onClick={props.onClose}
                disabled={loading}
                sx={{
                  minHeight: 44,
                  px: 2.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  color: 'text.primary',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'grey.50', borderColor: 'grey.400' }
                }}>
                {t('Cancel')}
              </Button>
            ) : null}
            <Button
              type="submit"
              disabled={loading}
              sx={{ ...raisedButtonSx, minWidth: 120 }}>
              {loading ? (
                <CircularProgress size={20} sx={{ color: 'common.white' }} />
              ) : props.influencer ? (
                t('Save')
              ) : (
                t('AddInfluencer')
              )}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  )
}
export default withTranslation()(Influencer)
